import { AlertTriangle, Loader2, CheckCircle2, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DuplicateWarningProps {
  isChecking: boolean;
  result: {
    is_duplicate: boolean;
    similar_entries: Array<{
      id: number;
      question_text: string;
      similarity_score: number;
    }>;
  } | null;
  onViewEntry?: (entryId: number) => void;
  className?: string;
}

function formatSimilarity(score: number): string {
  return `${Math.round(score * 100)}%`;
}

function getSimilarityColor(score: number): string {
  if (score >= 0.9) return 'text-red-600';
  if (score >= 0.8) return 'text-amber-600';
  return 'text-yellow-600';
}

export function DuplicateWarning({
  isChecking,
  result,
  onViewEntry,
  className,
}: DuplicateWarningProps) {
  // Show loading state
  if (isChecking) {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking for similar entries...
      </div>
    );
  }

  // No result yet
  if (!result) {
    return null;
  }

  // No duplicates found
  if (!result.is_duplicate || result.similar_entries.length === 0) {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-green-600', className)}>
        <CheckCircle2 className="h-4 w-4" />
        No similar entries found
      </div>
    );
  }

  // Duplicates found
  return (
    <div
      className={cn(
        'rounded-md border border-amber-200 bg-amber-50 p-3',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800">
            Similar entries found
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            Review the entries below to avoid duplicates. You can still proceed if this is intentional.
          </p>

          {/* Similar entries list */}
          <div className="mt-3 space-y-2">
            {result.similar_entries.slice(0, 3).map((entry) => (
              <div
                key={entry.id}
                className={cn(
                  'flex items-start gap-2 p-2 bg-white rounded border border-amber-100',
                  onViewEntry && 'cursor-pointer hover:bg-amber-50'
                )}
                onClick={() => onViewEntry?.(entry.id)}
              >
                <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm line-clamp-2" title={entry.question_text}>
                    {entry.question_text}
                  </p>
                </div>
                <span
                  className={cn(
                    'text-xs font-medium shrink-0',
                    getSimilarityColor(entry.similarity_score)
                  )}
                >
                  {formatSimilarity(entry.similarity_score)} match
                </span>
              </div>
            ))}
          </div>

          {result.similar_entries.length > 3 && (
            <p className="text-xs text-amber-700 mt-2">
              +{result.similar_entries.length - 3} more similar entries
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
